<template>
  <div class="position-relative">
    <!-- ================= FILTERS ================= -->
    <b-card class="mt-4">
      <b-row class="align-items-end">
        <b-col md="3">
          <b-form-group label="Report Name:" label-class="required-label mb-1" class="mb-2">
            <b-form-select v-model="filters.reportName" :options="reportNameOptions" class="form-control-sm"
              @change="onReportNameChange" />
          </b-form-group>
        </b-col>

        <b-col md="3">
          <b-form-group label="Academic Year:" label-class="required-label mb-1" class="mb-2">
            <b-form-select v-model="filters.academicYear" :options="$acedemicYears" class="form-control-sm"
              @change="onAcademicYearChange" />
          </b-form-group>
        </b-col>

        <b-col md="3">
          <b-form-group label="Programme:" label-class="required-label mb-1" class="mb-2">
            <b-form-select v-model="filters.usergroupId" :options="programmeOptions" class="form-control-sm"
              @change="onProgrammeChange" />
          </b-form-group>
        </b-col>

        <b-col md="3">
          <b-form-group label="Classes:" label-class="required-label mb-1" class="mb-2">
            <b-form-select v-model="filters.usersubgroupId" :options="subgroupOptionsWithAll" class="form-control-sm"
              :disabled="!filters.usergroupId || !filters.academicYear || loadingSubgroups"
              @change="onClassesChange" />
          </b-form-group>
        </b-col>

        <b-col md="3" v-if="filters.reportName === 'component_pending'">
          <b-form-group label="Component:" label-class="required-label mb-1" class="mb-2">
            <multiselect v-model="filters.componentNames" :options="componentOptions" :multiple="true"
              :close-on-select="false" :clear-on-select="false" :loading="loadingComponents"
              :disabled="!filters.usersubgroupId || loadingComponents" track-by="value" label="text"
              placeholder="Select components" select-label="" selected-label="" deselect-label="" />
          </b-form-group>
        </b-col>
      </b-row>

      <div class="text-right mt-2">
        <b-button variant="primary" size="sm" :disabled="!canGenerate || generating" @click="generateReport">
          <b-spinner v-if="generating" small class="mr-1"></b-spinner>
          {{ generating ? 'Generating...' : 'Generate Report' }}
        </b-button>
      </div>
    </b-card>

    <!-- ================= REPORT PREVIEW ================= -->
    <div v-if="submitted" class="mt-4">
      <div v-if="reportRows.length">
        <div class="d-flex justify-content-end mb-3">
          <b-button variant="success" size="sm" :disabled="exportBtn.disabled" @click="exportToExcel">
            <i class="fas fa-file-excel mr-1"></i>{{ exportBtn.text }}
          </b-button>
        </div>

        <div class="table-responsive">
          <table class="table table-sm table-striped table-bordered" ref="feeReportTable">
            <thead class="thead-dark">
              <tr>
                <th rowspan="3">#</th>
                <th rowspan="3">Student Name</th>
                <th rowspan="3">Admission No</th>
                <th rowspan="3">Phone</th>
                <th rowspan="3">Email</th>
                <th rowspan="3">Structure</th>
                <th v-if="componentNames.length" :colspan="componentNames.length * 7" class="text-center">
                  Component Details
                </th>
                <th rowspan="3">Total Amount</th>
                <th rowspan="3">Total Paid</th>
                <th rowspan="3">Total Due</th>
              </tr>
              <tr>
                <th v-for="name in componentNames" :key="name" colspan="7" class="text-center">{{ name }}</th>
              </tr>
              <tr>
                <template v-for="name in componentNames">
                  <th :key="`${name}-ta`">Total Amount</th>
                  <th :key="`${name}-bn`">Bill Name</th>
                  <th :key="`${name}-ba`">Bill Amount</th>
                  <th :key="`${name}-pa`">Paid Amount</th>
                  <th :key="`${name}-po`">Paid On</th>
                  <th :key="`${name}-bd`">Balance Due</th>
                  <th :key="`${name}-cd`">Component Due</th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in tableRows" :key="row.key">
                <template v-if="row.isFirstOfStudent">
                  <td :rowspan="row.group.rowCount">{{ row.group.serial }}</td>
                  <td :rowspan="row.group.rowCount">{{ row.group.student_name }}</td>
                  <td :rowspan="row.group.rowCount">{{ row.group.admission_no }}</td>
                  <td :rowspan="row.group.rowCount">{{ row.group.mobile }}</td>
                  <td :rowspan="row.group.rowCount">{{ row.group.email }}</td>
                  <td :rowspan="row.group.rowCount">{{ row.group.structure }}</td>
                </template>

                <template v-for="name in componentNames">
                  <template v-if="row.isFirstOfStudent">
                    <td :key="`${row.key}-${name}-ta`" :rowspan="row.group.rowCount" class="text-right">
                      {{ formatCell(componentTotalAmount(row.group, name)) }}
                    </td>
                  </template>
                  <td :key="`${row.key}-${name}-bn`">
                    {{ billCellAt(row.group, name, row.rowIndex, 'bill_name') || '-' }}
                  </td>
                  <td :key="`${row.key}-${name}-ba`" class="text-right">
                    {{ formatCell(billCellAt(row.group, name, row.rowIndex, 'bill_amount')) }}
                  </td>
                  <td :key="`${row.key}-${name}-pa`" class="text-right">
                    {{ formatCell(billCellAt(row.group, name, row.rowIndex, 'bill_paid_amount')) }}
                  </td>
                  <td :key="`${row.key}-${name}-po`">
                    {{ billCellAt(row.group, name, row.rowIndex, 'paid_on') || '-' }}
                  </td>
                  <td :key="`${row.key}-${name}-bd`" class="text-right">
                    {{ formatCell(billCellAt(row.group, name, row.rowIndex, 'balance_due')) }}
                  </td>
                  <template v-if="row.isFirstOfStudent">
                    <td :key="`${row.key}-${name}-cd`" :rowspan="row.group.rowCount" class="text-right">
                      {{ formatCell(componentDueFor(row.group, name)) }}
                    </td>
                  </template>
                </template>

                <template v-if="row.isFirstOfStudent">
                  <td :rowspan="row.group.rowCount" class="text-right">{{ row.group.total_amount }}</td>
                  <td :rowspan="row.group.rowCount" class="text-right">{{ row.group.total_paid }}</td>
                  <td :rowspan="row.group.rowCount" class="text-right">{{ row.group.total_due }}</td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <p v-else class="text-danger">No Data Found!</p>
    </div>
  </div>
</template>

<script>
import XLSX from "xlsx";

export default {
  name: "FeeReport",

  data() {
    return {
      reportNameOptions: [
        { value: "fee_structure_and_components", text: "Fee Structure and Components" },
        { value: "component_pending", text: "Component Pending Report" },
      ],
      filters: {
        reportName: "fee_structure_and_components",
        academicYear: null,
        usergroupId: null,
        usersubgroupId: null,
        componentNames: [],
      },

      programmeOptions: [{ value: null, text: "Select an option" }],
      subgroupOptions: [],
      loadingSubgroups: false,

      componentOptions: [],
      loadingComponents: false,

      generating: false,
      submitted: false,
      reportRows: [],

      exportBtn: {
        text: "Export to Excel",
        disabled: false,
      },
    };
  },

  computed: {
    canGenerate() {
      const baseValid = (
        this.filters.reportName &&
        this.filters.academicYear &&
        this.filters.usergroupId &&
        this.filters.usersubgroupId
      );

      if (!baseValid) return false;
      if (this.filters.reportName === "component_pending") return this.filters.componentNames.length > 0;
      return true;
    },

    subgroupOptionsWithAll() {
      return [
        { value: null, text: "Select an option" },
        { value: "all", text: "All Classes" },
        ...this.subgroupOptions,
      ];
    },

    // Every distinct component that has at least one real bill for SOME
    // student in the report, in first-seen order - these become the header
    // column groups. A component nobody has actually been billed for yet
    // (every occurrence is just the "Not Yet Billed" placeholder) is left
    // out entirely rather than showing an all-dash column. A student who
    // doesn't have a given (shown) component just shows blanks under it.
    componentNames() {
      const order = [];
      const hasRealBill = {};

      this.reportRows.forEach((student) => {
        (student.components || []).forEach((component) => {
          if (!(component.component_name in hasRealBill)) {
            hasRealBill[component.component_name] = false;
            order.push(component.component_name);
          }
          const billed = (component.bills || []).some((bill) => bill.bill_name !== "Not Yet Billed");
          if (billed) hasRealBill[component.component_name] = true;
        });
      });

      return order.filter((name) => hasRealBill[name]);
    },

    // One entry per student. rowCount is the most bills any single one of
    // their components has - that's how many rows this student needs, since
    // every component's column group shares the same row axis (component X's
    // 2nd bill sits in the same row as component Y's 2nd bill, even if Y
    // only has one). byName looks up a student's own component data.
    //
    // Total Amount / Total Paid / Total Due are the sum of Bill Amount /
    // Bill Paid Amount / Component Due across the student's own components
    // (not a separate API total), so they always agree with what's shown in
    // the rows. "Total Amount" is what's actually been billed so far, not
    // the structure's configured price - an unbilled component contributes
    // 0 until a bill exists for it.
    studentGroups() {
      return this.reportRows.map((student, index) => {
        const components = student.components || [];
        const byName = {};
        components.forEach((component) => { byName[component.component_name] = component; });

        const rowCount = Math.max(1, ...components.map((c) => (c.bills && c.bills.length) || 1));

        const componentTotals = components.map((component) => {
          const bills = component.bills || [];
          return {
            total: bills.reduce((sum, bill) => sum + (Number(bill.bill_amount) || 0), 0),
            paid: bills.reduce((sum, bill) => sum + (Number(bill.bill_paid_amount) || 0), 0),
          };
        });
        const totalAmount = Math.round(componentTotals.reduce((sum, c) => sum + c.total, 0) * 100) / 100;
        const totalPaid = Math.round(componentTotals.reduce((sum, c) => sum + c.paid, 0) * 100) / 100;
        const totalDue = Math.round(
          components.reduce((sum, c) => sum + (Number(c.component_due) || 0), 0) * 100
        ) / 100;

        return {
          key: index,
          serial: index + 1,
          student_name: student.student_name,
          admission_no: student.admission_no,
          mobile: student.mobile,
          email: student.email,
          structure: student.structure,
          byName,
          rowCount,
          total_amount: totalAmount,
          total_paid: totalPaid,
          total_due: totalDue,
        };
      });
    },

    // Flattens studentGroups into one entry per (student, row) pair - what
    // the template actually iterates over to render <tr>s.
    tableRows() {
      const rows = [];
      this.studentGroups.forEach((group) => {
        for (let rowIndex = 0; rowIndex < group.rowCount; rowIndex++) {
          rows.push({ key: `${group.key}-${rowIndex}`, group, rowIndex, isFirstOfStudent: rowIndex === 0 });
        }
      });
      return rows;
    },
  },

  async created() {
    await this.loadProgrammes();
  },

  methods: {
    async loadProgrammes() {
      const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups?allusergroups=1`;
      const response = await this.$axios.get(url);
      this.programmeOptions = [
        { value: null, text: "Select an option" },
        { value: "all", text: "All Programmes" },
        ...response.data.map((programme) => ({
          value: programme.id,
          text: `${programme.name} (${programme.code}) ${programme.scheme || ""}`,
        })),
      ];
    },

    onReportNameChange() {
      this.filters.componentNames = [];
      this.componentOptions = [];
      if (this.filters.reportName === "component_pending") this.loadComponentOptions();
    },

    onAcademicYearChange() {
      this.filters.usersubgroupId = null;
      this.filters.componentNames = [];
      this.componentOptions = [];
      this.loadSubgroups();
    },

    onProgrammeChange() {
      this.filters.usersubgroupId = null;
      this.filters.componentNames = [];
      this.componentOptions = [];
      this.loadSubgroups();
    },

    onClassesChange() {
      if (this.filters.reportName === "component_pending") this.loadComponentOptions();
    },

    async loadSubgroups() {
      this.subgroupOptions = [];
      if (!this.filters.usergroupId || !this.filters.academicYear) return;

      this.loadingSubgroups = true;
      try {
        const isAllProgrammes = this.filters.usergroupId === "all";
        const params = { academic_year: this.filters.academicYear };
        if (!isAllProgrammes) params.usergroup_id = this.filters.usergroupId;

        const url = `${this.$store.getters.getAPIKey.mainAPI}/feereports/classes`;
        const response = await this.$axios.get(url, { params });
        this.subgroupOptions = response.data
          .filter((subgroup) => subgroup.type === null)
          .map((subgroup) => ({
            value: subgroup.id,
            text: isAllProgrammes
              ? `${subgroup.usergroup ? subgroup.usergroup.name : ""} - ${subgroup.name} (${subgroup.code})`
              : `${subgroup.name} (${subgroup.code})`,
          }));
      } catch (error) {
        error;
        this.subgroupOptions = [];
      } finally {
        this.loadingSubgroups = false;
      }
    },

    // The classes filter is a single dropdown ("all" or one class id) - this
    // turns it into the array of usersubgroup ids the backend expects.
    selectedUsersubgroupIds() {
      return this.filters.usersubgroupId === "all"
        ? this.subgroupOptions.map((subgroup) => subgroup.value)
        : [this.filters.usersubgroupId];
    },

    async loadComponentOptions() {
      this.componentOptions = [];
      this.filters.componentNames = [];
      if (!this.filters.usersubgroupId || !this.filters.academicYear || !this.filters.usergroupId) return;

      this.loadingComponents = true;
      try {
        const url = `${this.$store.getters.getAPIKey.mainAPI}/feereports/components`;
        const payload = {
          academic_year: this.filters.academicYear,
          usergroup_id: this.filters.usergroupId,
          usersubgroup_ids: this.selectedUsersubgroupIds(),
        };
        const response = await this.$axios.post(url, payload);
        this.componentOptions = response.data.map((name) => ({ value: name, text: name }));
      } catch (error) {
        error;
        this.componentOptions = [];
      } finally {
        this.loadingComponents = false;
      }
    },

    async generateReport() {
      this.generating = true;
      this.submitted = false;
      this.reportRows = [];

      try {
        const url = `${this.$store.getters.getAPIKey.mainAPI}/feereports/structure-and-components`;
        const payload = {
          academic_year: this.filters.academicYear,
          usergroup_id: this.filters.usergroupId,
          usersubgroup_ids: this.selectedUsersubgroupIds(),
        };
        if (this.filters.reportName === "component_pending") {
          payload.component_names = this.filters.componentNames.map((component) => component.value);
          payload.pending_only = true;
        }
        const response = await this.$axios.post(url, payload);
        this.reportRows = response.data;
      } catch (error) {
        this.$toast.error(error.response?.data?.message || "Failed to generate report");
      } finally {
        this.submitted = true;
        this.generating = false;
      }
    },

    formatCell(value) {
      return value === null || value === undefined ? "-" : value;
    },

    // The bill at this row's position within one student's component,
    // or null if that student doesn't have this component, or it has fewer
    // bills than this row index.
    billCellAt(group, componentName, rowIndex, field) {
      const component = group.byName[componentName];
      const bill = component && (component.bills || [])[rowIndex];
      return bill ? bill[field] : null;
    },
    componentDueFor(group, componentName) {
      const component = group.byName[componentName];
      return component ? component.component_due : null;
    },
    // Sum of that component's own bills' Bill Amount - what's actually been
    // billed for it so far, same "Total Amount" definition used elsewhere
    // in this report (not the structure's configured price).
    componentTotalAmount(group, componentName) {
      const component = group.byName[componentName];
      if (!component) return null;
      const total = (component.bills || []).reduce((sum, bill) => sum + (Number(bill.bill_amount) || 0), 0);
      return Math.round(total * 100) / 100;
    },

    async exportToExcel() {
      this.exportBtn.text = "Please wait...";
      this.exportBtn.disabled = true;

      const { rows, merges, colCount } = this.buildExportSheet();
      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws["!merges"] = merges;
      ws["!cols"] = Array.from({ length: colCount }, (_, c) => {
        if (c === 1) return { wch: 20 }; // Student Name
        if (c === 4) return { wch: 22 }; // Email
        return { wch: 13 };
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Fee Report");

      this.exportBtn.text = "Export to Excel";
      this.exportBtn.disabled = false;
      XLSX.writeFile(wb, this.exportFileName());
    },

    exportFileName() {
      if (this.filters.reportName === "component_pending") {
        const names = this.filters.componentNames.map((component) => component.value).join("_") || "Component";
        return `Fee_Pending_Report_${names.replace(/[^a-z0-9]+/gi, "_")}.xlsx`;
      }
      return "Fee_Structure_and_Components_Report.xlsx";
    },

    // Builds the export sheet directly from componentNames/studentGroups
    // instead of scraping the rendered <table> via XLSX.utils.table_to_book
    // (that DOM-scraping path auto-detects date-looking text like
    // "2026-08-06" and silently rewrites it into an Excel date serial with a
    // spurious time component). Mirrors the on-screen table exactly: a
    // "Component Details" umbrella row over every component group, each
    // component's own colspan-ed name, its fixed 7 sub-columns (Total
    // Amount + Bill Name...Component Due), and one row per (student,
    // bill-position) pair - so the export never drifts from what's on
    // screen.
    buildExportSheet() {
      const fixedLeft = ["#", "Student Name", "Admission No", "Phone", "Email", "Structure"];
      const fixedRight = ["Total Amount", "Total Paid", "Total Due"];
      const subHeaders = ["Total Amount", "Bill Name", "Bill Amount", "Paid Amount", "Paid On", "Balance Due", "Component Due"];
      const names = this.componentNames;
      const colCount = fixedLeft.length + names.length * subHeaders.length + fixedRight.length;

      const headerRow0 = new Array(colCount).fill("");
      const headerRow1 = new Array(colCount).fill("");
      const headerRow2 = new Array(colCount).fill("");
      const merges = [];

      fixedLeft.forEach((label, c) => {
        headerRow0[c] = label;
        merges.push({ s: { r: 0, c }, e: { r: 2, c } });
      });

      let c = fixedLeft.length;
      // Per-component columns that summarize the whole component (rowspan'd
      // across the student's full row block, like Component Due): Total
      // Amount (first sub-column) and Component Due (last sub-column).
      const componentSummaryCols = [];
      if (names.length) {
        headerRow0[c] = "Component Details";
        merges.push({ s: { r: 0, c }, e: { r: 0, c: c + names.length * subHeaders.length - 1 } });
      }
      names.forEach((name) => {
        headerRow1[c] = name;
        merges.push({ s: { r: 1, c }, e: { r: 1, c: c + subHeaders.length - 1 } });
        subHeaders.forEach((label, i) => { headerRow2[c + i] = label; });
        componentSummaryCols.push(c, c + subHeaders.length - 1);
        c += subHeaders.length;
      });

      const tailStart = c;
      fixedRight.forEach((label, i) => {
        headerRow0[tailStart + i] = label;
        merges.push({ s: { r: 0, c: tailStart + i }, e: { r: 2, c: tailStart + i } });
      });

      const rows = [headerRow0, headerRow1, headerRow2];

      this.tableRows.forEach((row, index) => {
        const r = index + 3; // rows 0-2 are the header
        const group = row.group;

        if (row.isFirstOfStudent && group.rowCount > 1) {
          [...fixedLeft.map((_, i) => i), ...componentSummaryCols, ...fixedRight.map((_, i) => tailStart + i)]
            .forEach((col) => {
              merges.push({ s: { r, c: col }, e: { r: r + group.rowCount - 1, c: col } });
            });
        }

        const line = [
          row.isFirstOfStudent ? group.serial : "",
          row.isFirstOfStudent ? group.student_name : "",
          row.isFirstOfStudent ? group.admission_no : "",
          row.isFirstOfStudent ? group.mobile : "",
          row.isFirstOfStudent ? group.email : "",
          row.isFirstOfStudent ? group.structure : "",
        ];

        names.forEach((name) => {
          line.push(row.isFirstOfStudent ? this.formatCell(this.componentTotalAmount(group, name)) : "");
          line.push(this.billCellAt(group, name, row.rowIndex, "bill_name") || "-");
          line.push(this.formatCell(this.billCellAt(group, name, row.rowIndex, "bill_amount")));
          line.push(this.formatCell(this.billCellAt(group, name, row.rowIndex, "bill_paid_amount")));
          line.push(this.billCellAt(group, name, row.rowIndex, "paid_on") || "-");
          line.push(this.formatCell(this.billCellAt(group, name, row.rowIndex, "balance_due")));
          line.push(row.isFirstOfStudent ? this.formatCell(this.componentDueFor(group, name)) : "");
        });

        line.push(
          row.isFirstOfStudent ? group.total_amount : "",
          row.isFirstOfStudent ? group.total_paid : "",
          row.isFirstOfStudent ? group.total_due : ""
        );

        rows.push(line);
      });

      return { rows, merges, colCount };
    },
  },
};
</script>

<style scoped>
.required-label::after {
  content: '*';
  color: red;
  margin-left: 4px;
}
</style>
